import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import type { CreateTopupRequest } from '@ytclip/shared'
import { toast } from 'sonner'
import { ApiRequestError } from '@/lib/api-client.ts'
import { createTopup, fetchFeeRate, fetchTopupStatus } from './api.ts'

/** How often the QR dialog asks the API about a pending payment. */
const STATUS_POLL_MS = 3_000

export function useFeeRate() {
  return useQuery({ queryKey: ['fee-rate'], queryFn: fetchFeeRate, staleTime: 5 * 60_000 })
}

export function useCreateTopup() {
  return useMutation({
    mutationFn: (body: CreateTopupRequest) => createTopup(body),
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Gagal membuat pembayaran',
      ),
  })
}

/**
 * Watches one top-up until it stops being payable.
 *
 * The payment webhook usually settles the top-up server-side, so this poll is
 * mostly a cheap database read that notices it. The server throttles its own
 * upstream fallback checks, so polling a little eagerly here costs nothing.
 */
export function useTopupStatus(topupId: string | null) {
  const queryClient = useQueryClient()
  const forceNext = useRef(false)

  const query = useQuery({
    queryKey: ['topup', topupId],
    queryFn: async () => {
      const force = forceNext.current
      forceNext.current = false

      const topup = await fetchTopupStatus(topupId as string, force)

      if (topup.status === 'paid') {
        void queryClient.invalidateQueries({ queryKey: ['balance'] })
        void queryClient.invalidateQueries({ queryKey: ['transactions'] })
        void queryClient.invalidateQueries({ queryKey: ['membership'] })
      }

      return topup
    },
    enabled: Boolean(topupId),
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? STATUS_POLL_MS : false,
    staleTime: 0,
  })

  /**
   * Backs the "I have paid" button. Bypasses the server's poll throttle so the
   * answer reflects the gateway at this instant rather than up to a few seconds
   * ago — the difference matters when someone is staring at the screen.
   */
  const checkNow = useCallback(async () => {
    forceNext.current = true
    await query.refetch()
  }, [query])

  return { ...query, checkNow }
}
